var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { useFeatureGate, useStatsigClient } from "@statsig/react-bindings";
import LoadingSpinner from "@gcp-artifact/compass/lib/components/updated/loadingSpinner";
import { useAcl } from "@/api/auth/useAcl";
import config from "@/config";
import useAdminStore from "@/services/store/useAdminStore";
import { MixpanelEvents } from "@/utilities/mixpanel/events";
import { buildPartnerHubV2RedirectUrl, isIoLocalizationPending, isPartnerHubV2RedirectExcludedPath, markPartnerHubV2Redirect, PARTNER_HUB_V2_FORCE_REDIRECT_GATE, wasPartnerHubV2RedirectJustPerformed } from "@/utilities/partnerHubV2Redirect/partnerHubV2Redirect";
import { buildHubStatsigUser } from "@/utilities/statsig/hubStatsigUser";
var STATSIG_READY_TIMEOUT_MS = 3000;
var TRACKING_FLUSH_TIMEOUT_MS = 800;
var GATE_OPTIONS = {
    disableExposureLog: true
};
// Module scope: the shared client emits values_updated, which remounts us.
var appliedIdentityPartnerId = null;
var redirectStarted = false;
var exposureLoggedPartnerId = null;
export var __resetPartnerHubV2RedirectModuleState = function () {
    appliedIdentityPartnerId = null;
    redirectStarted = false;
    exposureLoggedPartnerId = null;
};
var hasPartnerIdentity = function (client, partnerId) {
    var _a, _b, _c;
    if (!partnerId)
        return false;
    if (appliedIdentityPartnerId === partnerId)
        return true;
    try {
        return ((_c = (_b = (_a = client.getContext) === null || _a === void 0 ? void 0 : _a.call(client).user) === null || _b === void 0 ? void 0 : _b.customIDs) === null || _c === void 0 ? void 0 : _c.partnerID) === partnerId;
    }
    catch (error) {
        return false;
    }
};
var getPartnerHubV2RedirectDecision = function (input) {
    if (!input.eligible)
        return { action: "pass" };
    // Before the Statsig branches: no partner id, no gate identity and no v2 url.
    if (!input.partnerId)
        return { action: "skip", reason: "no_partner_id" };
    // Before statsigReady: having given up, we stay failed open for this mount.
    if (input.statsigTimedOut) {
        return { action: "skip", reason: "statsig_timeout" };
    }
    if (!input.statsigReady)
        return { action: "hold" };
    if (!input.gateEnabled)
        return { action: "pass" };
    if (input.isLoopGuarded())
        return { action: "skip", reason: "loop_guard" };
    return { action: "redirect" };
};
var PartnerHubV2ForceRedirect = function (_a) {
    var children = _a.children, onRedirectingChange = _a.onRedirectingChange;
    var adminStore = useAdminStore().adminStore;
    var user = adminStore.currentUser;
    var location = useLocation();
    var aclPartnerId = useAcl().partnerId;
    var gate = useFeatureGate(PARTNER_HUB_V2_FORCE_REDIRECT_GATE, GATE_OPTIONS);
    var client = useStatsigClient().client;
    var partnerId = (user === null || user === void 0 ? void 0 : user.activePartner) || aclPartnerId || "";
    var _b = __read(React.useState(function () {
        return hasPartnerIdentity(client, partnerId);
    }), 2), identityApplied = _b[0], setIdentityApplied = _b[1];
    var _c = __read(React.useState(false), 2), statsigTimedOut = _c[0], setStatsigTimedOut = _c[1];
    var _d = __read(React.useState(false), 2), redirecting = _d[0], setRedirecting = _d[1];
    var mountedRef = React.useRef(true);
    var timedOutRef = React.useRef(false);
    var identityStartedRef = React.useRef(false);
    var redirectStartedRef = React.useRef(redirectStarted);
    var skippedReasonRef = React.useRef(null);
    var notifiedRedirectingRef = React.useRef(false);
    var onRedirectingChangeRef = React.useRef(onRedirectingChange);
    onRedirectingChangeRef.current = onRedirectingChange;
    var eligible = Boolean(user && user.isPartner && !user.isAdmin) &&
        !isPartnerHubV2RedirectExcludedPath(location.pathname) &&
        // ViewsContainer re-reads that saved path when the identity changes.
        !isIoLocalizationPending();
    var statsigReady = identityApplied && client.loadingStatus === "Ready";
    var eligibleRef = React.useRef(eligible);
    eligibleRef.current = eligible;
    // Frozen once redirecting: the fresh mark would read back as a loop guard.
    var decision = redirecting || redirectStartedRef.current
        ? { action: "redirect" }
        : getPartnerHubV2RedirectDecision({
            eligible: eligible,
            statsigReady: statsigReady,
            statsigTimedOut: statsigTimedOut,
            gateEnabled: gate.value,
            partnerId: partnerId,
            isLoopGuarded: wasPartnerHubV2RedirectJustPerformed
        });
    var holdingUser = decision.action === "hold" || decision.action === "redirect";
    React.useEffect(function () { return function () {
        mountedRef.current = false;
    }; }, []);
    React.useEffect(function () {
        if (redirectStartedRef.current)
            return;
        if (identityStartedRef.current)
            return;
        if (!eligible || !partnerId || !user)
            return;
        identityStartedRef.current = true;
        if (hasPartnerIdentity(client, partnerId)) {
            setIdentityApplied(true);
            return;
        }
        var statsigUser = buildHubStatsigUser(user, partnerId);
        // updateUserAsync flips the shared client to Loading and unmounts us.
        client.dataAdapter
            .prefetchData(statsigUser)
            .catch(function () { return undefined; })
            .then(function () {
            if (!mountedRef.current ||
                timedOutRef.current ||
                !eligibleRef.current) {
                return;
            }
            var applied = false;
            try {
                if (client.dataAdapter.getDataSync(statsigUser)) {
                    client.updateUserSync(statsigUser);
                    applied = true;
                }
            }
            catch (error) { }
            if (applied) {
                appliedIdentityPartnerId = partnerId;
                setIdentityApplied(true);
            }
            else
                setStatsigTimedOut(true);
        });
    }, [eligible, partnerId, user]);
    React.useEffect(function () {
        if (!eligible || !partnerId || statsigReady || statsigTimedOut)
            return;
        var timer = setTimeout(function () {
            timedOutRef.current = true;
            setStatsigTimedOut(true);
        }, STATSIG_READY_TIMEOUT_MS);
        return function () {
            clearTimeout(timer);
        };
    }, [eligible, partnerId, statsigReady, statsigTimedOut]);
    React.useEffect(function () {
        if (redirectStartedRef.current)
            return;
        if (eligible &&
            partnerId &&
            statsigReady &&
            !statsigTimedOut &&
            exposureLoggedPartnerId !== partnerId) {
            exposureLoggedPartnerId = partnerId;
            client.getFeatureGate(PARTNER_HUB_V2_FORCE_REDIRECT_GATE);
        }
        var current = getPartnerHubV2RedirectDecision({
            eligible: eligible,
            statsigReady: statsigReady,
            statsigTimedOut: statsigTimedOut,
            gateEnabled: gate.value,
            partnerId: partnerId,
            isLoopGuarded: wasPartnerHubV2RedirectJustPerformed
        });
        if (current.action === "skip") {
            if (skippedReasonRef.current !== current.reason) {
                skippedReasonRef.current = current.reason;
                MixpanelEvents.partnerHubForceRedirectSkipped({
                    partnerId: partnerId || undefined,
                    path: location.pathname,
                    reason: current.reason
                });
            }
            return;
        }
        if (current.action !== "redirect")
            return;
        redirectStarted = true;
        redirectStartedRef.current = true;
        setRedirecting(true);
        var _a = buildPartnerHubV2RedirectUrl({
            pathname: location.pathname,
            search: location.search,
            partnerId: partnerId,
            baseUrl: config.hubV2AppUrl
        }), url = _a.url, mapping = _a.mapping;
        markPartnerHubV2Redirect();
        // Awaited: the browser drops in-flight requests when we navigate away.
        var leaveForV2 = function () { return __awaiter(void 0, void 0, void 0, function () {
            var error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        return [4 /*yield*/, Promise.race([
                                MixpanelEvents.partnerHubForceRedirectedToV2({
                                    partnerId: partnerId,
                                    fromPath: "".concat(location.pathname).concat(location.search),
                                    toPath: url,
                                    mappingKind: mapping.kind,
                                    mappingRule: mapping.rule,
                                    gateRuleId: gate.ruleID,
                                    gateReason: gate.details && gate.details.reason
                                }),
                                new Promise(function (resolve) {
                                    setTimeout(resolve, TRACKING_FLUSH_TIMEOUT_MS);
                                })
                            ])];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        error_1 = _a.sent();
                        return [3 /*break*/, 3];
                    case 3:
                        window.location.replace(url);
                        return [2 /*return*/];
                }
            });
        }); };
        leaveForV2();
    }, [
        eligible,
        statsigReady,
        gate.value,
        partnerId,
        location.pathname,
        location.search,
        statsigTimedOut
    ]);
    React.useEffect(function () {
        var notify = onRedirectingChangeRef.current;
        if (notifiedRedirectingRef.current !== holdingUser) {
            notifiedRedirectingRef.current = holdingUser;
            if (notify)
                notify(holdingUser);
        }
        // The host's flag must not outlive this component.
        return function () {
            if (!notifiedRedirectingRef.current)
                return;
            notifiedRedirectingRef.current = false;
            var notifyOnCleanup = onRedirectingChangeRef.current;
            if (notifyOnCleanup)
                notifyOnCleanup(false);
        };
    }, [holdingUser]);
    if (holdingUser)
        return React.createElement(LoadingSpinner, null);
    return React.createElement(React.Fragment, null, children);
};
export default observer(PartnerHubV2ForceRedirect);
//# sourceMappingURL=data:application/json;base64,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